import React, { useState, useEffect, useMemo } from 'react';
import { Card, Table, Form, Button, Row, Col, Badge, Spinner, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import moment from 'moment';
import { toast } from 'react-toastify';
import { BANGDANHGIA_HUONGDANTHUCHANH_API, FILE_MANAGER_API, BaseGatewayImage } from '../../../../utils/apiUrl';
import { Get, Post, PostFormData } from '../../../../utils/httpProvider';
import ImportBangDanhGiaThucHanhModal from './ImportBangDanhGiaThucHanhModal';

const BangDanhGiaThucHanhTab = ({ lopId }) => {
    const queryClient = useQueryClient();
    const [selectedBatchId, setSelectedBatchId] = useState('');
    const [selectedDonViId, setSelectedDonViId] = useState(null);
    const [items, setItems] = useState([]);
    const [fileMinhChungPath, setFileMinhChungPath] = useState('');
    const [fileMinhChungName, setFileMinhChungName] = useState('');
    const [isUploadingFile, setIsUploadingFile] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);

    // Mobile Stepper & Search State
    const [searchTerm, setSearchTerm] = useState('');
    const [mobileIndex, setMobileIndex] = useState(0);

    const getInitials = (name) => {
        if (!name) return 'HV';
        const parts = name.trim().split(/\s+/);
        if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    };

    const totalCount = items.length;
    const datCount = items.filter(it => it.ketQuaDanhGia === 'Đạt').length;
    const khongDatCount = items.filter(it => it.ketQuaDanhGia === 'Không đạt').length;

    const filteredItems = useMemo(() => {
        if (!searchTerm.trim()) return items;
        const key = searchTerm.toLowerCase().trim();
        return items.filter(it =>
            (it.hoTen && it.hoTen.toLowerCase().includes(key)) ||
            (it.soCccd && it.soCccd.includes(key))
        );
    }, [items, searchTerm]);

    const currentMobileItem = filteredItems[mobileIndex] || null;

    // 1. Fetch Danh sách các Đợt / Khoa thực hành của Lớp
    const { data: dotList = [], isLoading: isLoadingDots } = useQuery(
        ['danhSachDotThucHanh', lopId],
        () => Get(`${BANGDANHGIA_HUONGDANTHUCHANH_API.GET_DANH_SACH_DOT}/${lopId}`).then(res => res?.$values || res || []),
        {
            enabled: !!lopId,
            staleTime: 0,
            cacheTime: 0
        }
    );

    // Đồng bộ chọn đợt đầu tiên khi có dotList
    useEffect(() => {
        if (dotList && dotList.length > 0) {
            const exists = dotList.find(d => d.batchId === selectedBatchId);
            if (!selectedBatchId || !exists) {
                setSelectedBatchId(dotList[0].batchId);
                setSelectedDonViId(dotList[0].donViId);
            }
        }
    }, [dotList, selectedBatchId]);

    // 2. Fetch Bảng đánh giá của Đợt đang chọn
    const { data: dotData, isLoading: isLoadingDotData, refetch: refetchDotData } = useQuery(
        ['bangDanhGiaDot', lopId, selectedBatchId],
        () => Get(`${BANGDANHGIA_HUONGDANTHUCHANH_API.GET_BANG_DANH_GIA}/${lopId}/${selectedBatchId}`).then(res => res?.data || res),
        {
            enabled: !!lopId && !!selectedBatchId,
            staleTime: 0,
            cacheTime: 0
        }
    );

    // Đồng bộ dữ liệu bảng điểm và file minh chứng khi dotData thay đổi
    useEffect(() => {
        if (dotData) {
            const list = dotData?.items?.$values || dotData?.items || [];
            setItems(list);
            setFileMinhChungPath(dotData?.fileMinhChungKhoaPath || '');
            setFileMinhChungName(dotData?.fileMinhChungKhoaName || '');
            setMobileIndex(0);
        }
    }, [dotData]);

    const activeDot = dotList.find(d => d.batchId === selectedBatchId);

    // Thay đổi giá trị trường của 1 học viên
    const handleFieldChange = (index, field, value) => {
        setItems(prev => {
            const next = [...prev];
            next[index] = {
                ...next[index],
                [field]: value
            };
            return next;
        });
    };

    // Upload file minh chứng chung của Đợt
    const handleUploadFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            setIsUploadingFile(true);
            const formData = new FormData();
            formData.append('file', file);
            formData.append('folder', 'bangdanhgia_thuchanh');

            const uploadUrl = FILE_MANAGER_API?.UPLOAD || '/api/FileManager/UploadSingle';
            const uploadRes = await PostFormData(uploadUrl, formData);
            const uploaded = Array.isArray(uploadRes) ? uploadRes[0] : (uploadRes?.data?.[0] || uploadRes?.data || uploadRes);
            const path = uploaded?.filePath || uploaded?.FilePath || uploaded?.path || uploaded?.fileUrl || uploaded?.url || uploadRes?.path || uploadRes?.filePath || uploadRes?.fileUrl || '';
            const name = file.name;

            if (path) {
                setFileMinhChungPath(path);
                setFileMinhChungName(name);
                toast.success(`Đã đính kèm file minh chứng: ${name}`);
            } else {
                toast.error("Không nhận được đường dẫn file từ máy chủ!");
            }
        } catch (err) {
            toast.error(err?.response?.data?.message || "Lỗi khi upload file minh chứng!");
        } finally {
            setIsUploadingFile(false);
            if (e.target) e.target.value = '';
        }
    };

    // Xóa file minh chứng
    const handleRemoveFile = () => {
        setFileMinhChungPath('');
        setFileMinhChungName('');
        toast.info("Đã gỡ file minh chứng. Hãy bấm 'Lưu bảng đánh giá' để cập nhật.");
    };

    // Đồng bộ danh sách học viên từ lịch thực hành
    const syncMutation = useMutation(
        () => Post(`${BANGDANHGIA_HUONGDANTHUCHANH_API.SYNC_FROM_SCHEDULE}/${lopId}/${selectedBatchId}`, null, {}),
        {
            onSuccess: (res) => {
                const list = res?.items?.$values || res?.items || [];
                setItems(list);
                toast.success("Đồng bộ danh sách học viên từ lịch thực hành thành công!");
                queryClient.invalidateQueries(['bangDanhGiaDot', lopId, selectedBatchId]);
            },
            onError: (err) => {
                toast.error(err?.response?.data?.message || err?.message || "Lỗi khi đồng bộ lịch học!");
            }
        }
    );

    // Lưu bảng đánh giá của đợt
    const handleSave = async (showToast = true) => {
        if (!selectedBatchId) {
            toast.warning("Vui lòng chọn đợt thực hành cần lưu!");
            return false;
        }

        try {
            setIsSaving(true);
            const payload = {
                lopDaoTaoThucHanhId: lopId,
                batchId: selectedBatchId,
                donViId: selectedDonViId || activeDot?.donViId,
                fileMinhChungKhoaPath: fileMinhChungPath,
                fileMinhChungKhoaName: fileMinhChungName,
                items: items.map(it => ({
                    id: it.id,
                    lopDaoTaoThucHanhId: lopId,
                    batchId: selectedBatchId,
                    donViId: selectedDonViId || activeDot?.donViId,
                    hoSoTuyenSinhId: it.hoSoTuyenSinhId,
                    nangLucChuyenMon: it.nangLucChuyenMon || '',
                    yThucKyLuat: it.yThucKyLuat || '',
                    ketQuaDanhGia: it.ketQuaDanhGia || '',
                    nhanXetChung: it.nhanXetChung || ''
                }))
            };

            await Post(BANGDANHGIA_HUONGDANTHUCHANH_API.SAVE_BATCH, null, payload);
            if (showToast) {
                toast.success("Đã lưu bảng đánh giá thành công!");
            }
            queryClient.invalidateQueries(['bangDanhGiaDot', lopId, selectedBatchId]);
            return true;
        } catch (err) {
            toast.error(err?.response?.data?.message || "Lỗi khi lưu bảng đánh giá!");
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    // Mobile Stepper: Lưu & Tiếp tục sang học viên tiếp theo
    const handleSaveAndNext = async () => {
        const saved = await handleSave(false);
        if (saved) {
            if (mobileIndex < filteredItems.length - 1) {
                setMobileIndex(prev => prev + 1);
                toast.success(`Đã lưu & chuyển sang học viên ${mobileIndex + 2}/${filteredItems.length}`);
            } else {
                toast.success("Đã lưu học viên cuối cùng trong đợt!");
            }
        }
    };

    // Áp dụng dữ liệu import từ Excel
    const handleApplyImport = (importedRows) => {
        const normalize = (str) => (str || '').trim().toLowerCase().replace(/\s+/g, ' ');
        setItems(prev => {
            const next = [...prev];
            importedRows.forEach(row => {
                const matchIndex = next.findIndex(hv => {
                    if (row.soCccd && hv.soCccd && row.soCccd === hv.soCccd) return true;
                    if (row.hoTen && hv.hoTen && normalize(row.hoTen) === normalize(hv.hoTen)) return true;
                    return false;
                });

                if (matchIndex !== -1) {
                    next[matchIndex] = {
                        ...next[matchIndex],
                        nangLucChuyenMon: row.nangLucChuyenMon || next[matchIndex].nangLucChuyenMon,
                        yThucKyLuat: row.yThucKyLuat || next[matchIndex].yThucKyLuat,
                        ketQuaDanhGia: row.ketQuaDanhGia || next[matchIndex].ketQuaDanhGia,
                        nhanXetChung: row.nhanXetChung || next[matchIndex].nhanXetChung
                    };
                }
            });
            return next;
        });
    };

    // Xuất bảng đánh giá ra file Excel / CSV
    const handleExportExcel = () => {
        if (!items || items.length === 0) {
            toast.warning("Không có dữ liệu để xuất!");
            return;
        }

        let csvContent = "\ufeff";
        csvContent += "STT,Họ và tên,Ngày sinh,Số CCCD,Đơn vị / Nơi sinh,Năng lực thực hành chuyên khoa,Ý thức tổ chức kỷ luật,Kết quả đánh giá,Ghi chú / Nhận xét thêm\n";

        items.forEach((it, idx) => {
            const ngaySinh = it.ngaySinh ? moment(it.ngaySinh).format('DD/MM/YYYY') : '';
            const hoTen = `"${(it.hoTen || '').replace(/"/g, '""')}"`;
            const cccd = `"${(it.soCccd || '').replace(/"/g, '""')}"`;
            const donVi = `"${(it.donViCongTac || '').replace(/"/g, '""')}"`;
            const nangLuc = `"${(it.nangLucChuyenMon || '').replace(/"/g, '""')}"`;
            const kyLuat = `"${(it.yThucKyLuat || '').replace(/"/g, '""')}"`;
            const ketQua = `"${(it.ketQuaDanhGia || '').replace(/"/g, '""')}"`;
            const ghiChu = `"${(it.nhanXetChung || '').replace(/"/g, '""')}"`;

            csvContent += `${idx + 1},${hoTen},${ngaySinh},${cccd},${donVi},${nangLuc},${kyLuat},${ketQua},${ghiChu}\n`;
        });

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        const tenFile = `BangDanhGia_${activeDot?.tenKhoa ? activeDot.tenKhoa.replace(/[^a-zA-Z0-9]/g, '_') : 'Dot'}_${moment().format('YYYYMMDD_HHmm')}.csv`;
        link.setAttribute("href", url);
        link.setAttribute("download", tenFile);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success(`Đã xuất file: ${tenFile}`);
    };

    return (
        <div>
            {/* 1. TOP BAR DESKTOP (>= 768px): Chọn Đợt / Khoa thực hành & Toolbar */}
            <div className="d-none d-md-block">
                <Card className="shadow-sm border-0 mb-3">
                    <Card.Body className="py-2">
                        <Row className="align-items-center justify-content-between">
                            <Col lg={4} md={5} className="mb-2 mb-md-0">
                                <div className="d-flex align-items-center">
                                    <span className="font-weight-bold text-dark small mr-2 text-nowrap">
                                        <i className="fas fa-layer-group text-primary mr-1"></i> Đợt:
                                    </span>
                                    {dotList.length === 0 ? (
                                        <Form.Control as="select" size="sm" disabled className="text-muted">
                                            <option>Chưa có lịch thực hành</option>
                                        </Form.Control>
                                    ) : (
                                        <Form.Control
                                            as="select"
                                            size="sm"
                                            className="font-weight-bold text-primary"
                                            value={selectedBatchId}
                                            onChange={(e) => {
                                                setSelectedBatchId(e.target.value);
                                                const dot = dotList.find(d => d.batchId === e.target.value);
                                                if (dot) setSelectedDonViId(dot.donViId);
                                                setMobileIndex(0);
                                            }}
                                        >
                                            {dotList.map((dot) => (
                                                <option key={dot.batchId} value={dot.batchId}>
                                                    {dot.tenDotHienThi}
                                                </option>
                                            ))}
                                        </Form.Control>
                                    )}
                                </div>
                            </Col>

                            {/* File minh chứng của Đợt */}
                            <Col lg={3} md={7} className="mb-2 mb-lg-0">
                                <div className="d-flex align-items-center">
                                    <span className="font-weight-bold small text-dark mr-2 text-nowrap">
                                        <i className="fas fa-file-contract text-danger mr-1"></i> File:
                                    </span>
                                    {fileMinhChungPath ? (
                                        <div className="d-flex align-items-center bg-light px-2 py-1 rounded border">
                                            <a
                                                href={fileMinhChungPath.startsWith('http') ? fileMinhChungPath : `${BaseGatewayImage}/${fileMinhChungPath}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-primary font-weight-bold small text-truncate mr-2"
                                                style={{ maxWidth: '140px' }}
                                                title={fileMinhChungName}
                                            >
                                                <i className="fas fa-paperclip mr-1"></i>
                                                {fileMinhChungName || "Xem file"}
                                            </a>
                                            <OverlayTrigger placement="top" overlay={<Tooltip>Gỡ file</Tooltip>}>
                                                <Button variant="link" size="sm" className="p-0 text-danger" onClick={handleRemoveFile}>
                                                    <i className="fas fa-times"></i>
                                                </Button>
                                            </OverlayTrigger>
                                        </div>
                                    ) : (
                                        <label className="btn btn-outline-secondary btn-sm mb-0 cursor-pointer py-1 px-2">
                                            <i className="fas fa-upload mr-1"></i>
                                            {isUploadingFile ? "Đang tải..." : "Upload file minh chứng"}
                                            <input
                                                type="file"
                                                style={{ display: 'none' }}
                                                onChange={handleUploadFile}
                                                disabled={isUploadingFile}
                                            />
                                        </label>
                                    )}
                                </div>
                            </Col>

                            {/* Action Buttons */}
                            <Col lg={5} md={12} className="text-lg-right text-left mt-2 mt-lg-0">
                                <Button
                                    variant="outline-info"
                                    size="sm"
                                    className="mr-2 mb-1"
                                    onClick={() => syncMutation.mutate()}
                                    disabled={syncMutation.isLoading || !selectedBatchId}
                                >
                                    <i className={`fas fa-sync-alt mr-1 ${syncMutation.isLoading ? 'fa-spin' : ''}`}></i>
                                    Đồng bộ từ Lịch
                                </Button>

                                <Button
                                    variant="outline-success"
                                    size="sm"
                                    className="mr-2 mb-1"
                                    onClick={() => setShowImportModal(true)}
                                    disabled={!selectedBatchId}
                                >
                                    <i className="fas fa-file-excel mr-1"></i>
                                    Nhập Excel
                                </Button>

                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="mr-2 mb-1"
                                    onClick={handleExportExcel}
                                    disabled={items.length === 0}
                                >
                                    <i className="fas fa-download mr-1"></i>
                                    Xuất Excel
                                </Button>

                                <Button
                                    variant="primary"
                                    size="sm"
                                    className="mb-1 font-weight-bold"
                                    onClick={() => handleSave(true)}
                                    disabled={isSaving || !selectedBatchId}
                                >
                                    <i className={`fas fa-save mr-1 ${isSaving ? 'fa-spin' : ''}`}></i>
                                    {isSaving ? "Đang lưu..." : "Lưu"}
                                </Button>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            </div>

            {/* 2. DỮ LIỆU ĐÁNH GIÁ: BẢNG SPREADSHEET */}
            {isLoadingDotData ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" size="sm" />
                    <span className="ml-2 text-muted">Đang tải dữ liệu đánh giá...</span>
                </div>
            ) : (
                <>
                    {/* --- GIAO DIỆN DESKTOP: SPREADSHEET VIEW --- */}
                    <div className="d-none d-md-block">
                        <Card className="shadow-sm border-0">
                            <Card.Body className="p-0">
                                <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                                    <Table hover bordered className="mb-0 text-nowrap align-middle">
                                        <thead className="thead-light sticky-top" style={{ zIndex: 5 }}>
                                            <tr>
                                                <th style={{ width: 45 }} className="text-center">#</th>
                                                <th style={{ width: 180 }}>Họ và tên</th>
                                                <th style={{ width: 100 }} className="text-center">Ngày sinh</th>
                                                <th style={{ width: 110 }}>Số CCCD</th>
                                                <th style={{ minWidth: 260 }}>1. Năng lực chuyên khoa</th>
                                                <th style={{ minWidth: 260 }}>2. Ý thức, kỷ luật</th>
                                                <th style={{ width: 140 }} className="text-center">Kết quả</th>
                                                <th style={{ minWidth: 160 }}>Ghi chú / Nhận xét thêm</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {items.length === 0 ? (
                                                <tr>
                                                    <td colSpan={8} className="text-center py-4 text-muted">
                                                        <i className="fas fa-inbox mr-1"></i> Chưa có dữ liệu học viên trong đợt này. Bấm <strong>"Đồng bộ từ Lịch"</strong> để nạp danh sách.
                                                    </td>
                                                </tr>
                                            ) : (
                                                items.map((hv, idx) => (
                                                    <tr key={hv.hoSoTuyenSinhId || idx}>
                                                        <td className="text-center font-weight-bold text-muted">{idx + 1}</td>
                                                        <td>
                                                            <div className="font-weight-bold text-dark">{hv.hoTen}</div>
                                                            <small className="text-muted">{hv.donViCongTac || ""}</small>
                                                        </td>
                                                        <td className="text-center text-muted small">
                                                            {hv.ngaySinh ? moment(hv.ngaySinh).format('DD/MM/YYYY') : "—"}
                                                        </td>
                                                        <td className="text-muted small">{hv.soCccd || "—"}</td>

                                                        {/* 1. Năng lực thực hành chuyên môn */}
                                                        <td>
                                                            <Form.Control
                                                                as="textarea"
                                                                rows={2}
                                                                size="sm"
                                                                placeholder="Nhận xét năng lực chuyên môn..."
                                                                value={hv.nangLucChuyenMon || ''}
                                                                onChange={(e) => handleFieldChange(idx, 'nangLucChuyenMon', e.target.value)}
                                                                style={{ fontSize: '13px', resize: 'vertical' }}
                                                            />
                                                        </td>

                                                        {/* 2. Ý thức, tổ chức kỷ luật */}
                                                        <td>
                                                            <Form.Control
                                                                as="textarea"
                                                                rows={2}
                                                                size="sm"
                                                                placeholder="Nhận xét ý thức kỷ luật..."
                                                                value={hv.yThucKyLuat || ''}
                                                                onChange={(e) => handleFieldChange(idx, 'yThucKyLuat', e.target.value)}
                                                                style={{ fontSize: '13px', resize: 'vertical' }}
                                                            />
                                                        </td>

                                                        {/* Kết quả Đạt / Không đạt */}
                                                        <td className="text-center">
                                                            <Form.Control
                                                                as="select"
                                                                size="sm"
                                                                className={`font-weight-bold text-center ${hv.ketQuaDanhGia === 'Đạt' ? 'text-success' :
                                                                        hv.ketQuaDanhGia === 'Không đạt' ? 'text-danger' : 'text-muted'
                                                                    }`}
                                                                value={hv.ketQuaDanhGia || ''}
                                                                onChange={(e) => handleFieldChange(idx, 'ketQuaDanhGia', e.target.value)}
                                                            >
                                                                <option value="">-- Chọn --</option>
                                                                <option value="Đạt">Đạt</option>
                                                                <option value="Không đạt">Không đạt</option>
                                                            </Form.Control>
                                                        </td>

                                                        {/* Ghi chú thêm */}
                                                        <td>
                                                            <Form.Control
                                                                type="text"
                                                                size="sm"
                                                                placeholder="Ghi chú..."
                                                                value={hv.nhanXetChung || ''}
                                                                onChange={(e) => handleFieldChange(idx, 'nhanXetChung', e.target.value)}
                                                                style={{ fontSize: '13px' }}
                                                            />
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </Card.Body>
                        </Card>
                    </div>

                    {/* ========================================================================= */}
                    {/* --- GIAO DIỆN MOBILE (< 768px): STEPPER CARD CHUẨN MOCKUP ---             */}
                    {/* ========================================================================= */}
                    <div className="d-block d-md-none">
                        {/* 1. Chọn Đợt / Khoa thực hành */}
                        <div className="bg-white rounded-lg p-2 mb-2 border shadow-sm" style={{ borderRadius: '10px' }}>
                            <div className="d-flex align-items-center">
                                <span className="font-weight-bold text-dark small mr-2 text-nowrap" style={{ fontSize: '12px' }}>
                                    <i className="fas fa-layer-group text-primary mr-1"></i> Đợt:
                                </span>
                                {dotList.length === 0 ? (
                                    <Form.Control as="select" size="sm" disabled className="text-muted" style={{ fontSize: '12px' }}>
                                        <option>Chưa có lịch thực hành</option>
                                    </Form.Control>
                                ) : (
                                    <Form.Control
                                        as="select"
                                        size="sm"
                                        className="font-weight-bold text-primary"
                                        style={{ fontSize: '12px' }}
                                        value={selectedBatchId}
                                        onChange={(e) => {
                                            setSelectedBatchId(e.target.value);
                                            const dot = dotList.find(d => d.batchId === e.target.value);
                                            if (dot) setSelectedDonViId(dot.donViId);
                                            setMobileIndex(0);
                                        }}
                                    >
                                        {dotList.map((dot) => (
                                            <option key={dot.batchId} value={dot.batchId}>
                                                {dot.tenDotHienThi}
                                            </option>
                                        ))}
                                    </Form.Control>
                                )}
                            </div>
                        </div>

                        {/* 2. File Minh Chứng Card */}
                        <div className="bg-white rounded-lg p-3 mb-2 border shadow-sm" style={{ borderRadius: '12px' }}>
                            <div className="d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center">
                                    <div
                                        className="d-flex align-items-center justify-content-center mr-2 font-weight-bold text-primary"
                                        style={{ width: '36px', height: '36px', borderRadius: '8px', backgroundColor: '#e0f2fe', fontSize: '16px' }}
                                    >
                                        W
                                    </div>
                                    <div>
                                        <div className="font-weight-bold text-dark text-truncate" style={{ fontSize: '13px', maxWidth: '170px' }}>
                                            {fileMinhChungName || 'Chưa có file minh chứng'}
                                        </div>
                                        <div className="text-muted small" style={{ fontSize: '11px' }}>
                                            {fileMinhChungPath ? 'Đã tải lên' : 'Chạm để tải file lên'}
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    {fileMinhChungPath ? (
                                        <div className="d-flex align-items-center">
                                            <a
                                                href={fileMinhChungPath.startsWith('http') ? fileMinhChungPath : `${BaseGatewayImage}/${fileMinhChungPath}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="btn btn-sm btn-outline-primary py-1 px-2 mr-1"
                                                style={{ fontSize: '12px' }}
                                            >
                                                Xem
                                            </a>
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                className="py-1 px-2"
                                                style={{ fontSize: '12px' }}
                                                onClick={handleRemoveFile}
                                            >
                                                ✕
                                            </Button>
                                        </div>
                                    ) : (
                                        <label className="btn btn-sm btn-primary mb-0 py-1 px-2 cursor-pointer" style={{ fontSize: '12px' }}>
                                            {isUploadingFile ? 'Đang tải...' : 'Tải lên'}
                                            <input
                                                type="file"
                                                hidden
                                                accept=".docx, .doc, .pdf, .xlsx, .xls, .png, .jpg, .jpeg"
                                                onChange={handleUploadFile}
                                                disabled={isUploadingFile}
                                            />
                                        </label>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* 3. Ba Thẻ Thống Kê Nhanh */}
                        <Row className="g-2 mb-2">
                            <Col xs={4}>
                                <div className="bg-white p-2 border rounded shadow-sm text-center" style={{ borderRadius: '10px' }}>
                                    <div className="font-weight-bold text-primary" style={{ fontSize: '18px' }}>{totalCount}</div>
                                    <div className="text-muted" style={{ fontSize: '11px' }}>Học viên</div>
                                </div>
                            </Col>
                            <Col xs={4}>
                                <div className="bg-white p-2 border rounded shadow-sm text-center" style={{ borderRadius: '10px' }}>
                                    <div className="font-weight-bold text-success" style={{ fontSize: '18px' }}>{datCount}</div>
                                    <div className="text-muted" style={{ fontSize: '11px' }}>Đạt</div>
                                </div>
                            </Col>
                            <Col xs={4}>
                                <div className="bg-white p-2 border rounded shadow-sm text-center" style={{ borderRadius: '10px' }}>
                                    <div className="font-weight-bold text-danger" style={{ fontSize: '18px' }}>{khongDatCount}</div>
                                    <div className="text-muted" style={{ fontSize: '11px' }}>Không đạt</div>
                                </div>
                            </Col>
                        </Row>

                        {/* 4. Toolbar: Search & Nút Action */}
                        <div className="bg-white p-2 border rounded shadow-sm mb-2" style={{ borderRadius: '10px' }}>
                            <Form.Control
                                size="sm"
                                type="text"
                                placeholder="Tìm học viên..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setMobileIndex(0);
                                }}
                                className="mb-2"
                                style={{ fontSize: '13px' }}
                            />
                            <div className="d-flex justify-content-between">
                                <Button
                                    variant="outline-info"
                                    size="sm"
                                    className="py-1 px-2"
                                    style={{ fontSize: '12px' }}
                                    onClick={() => syncMutation.mutate()}
                                    disabled={syncMutation.isLoading || !selectedBatchId}
                                >
                                    <i className={`fas fa-sync-alt mr-1 ${syncMutation.isLoading ? 'fa-spin' : ''}`}></i> Đồng bộ
                                </Button>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    className="py-1 px-3"
                                    style={{ fontSize: '12px' }}
                                    onClick={() => setShowImportModal(true)}
                                    disabled={!selectedBatchId}
                                >
                                    <i className="fas fa-file-excel mr-1 text-success"></i> Excel
                                </Button>
                                <Button
                                    variant="outline-success"
                                    size="sm"
                                    className="py-1 px-3"
                                    style={{ fontSize: '12px' }}
                                    onClick={handleExportExcel}
                                    disabled={items.length === 0}
                                >
                                    <i className="fas fa-download mr-1"></i> Xuất
                                </Button>
                            </div>
                        </div>

                        {/* 5. Điều hướng Stepper Chuyển Học Viên */}
                        {filteredItems.length > 0 && (
                            <div className="d-flex align-items-center justify-content-between bg-white px-3 py-2 border rounded shadow-sm mb-2" style={{ borderRadius: '10px' }}>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={mobileIndex <= 0}
                                    onClick={() => setMobileIndex(prev => Math.max(0, prev - 1))}
                                    style={{ fontSize: '12px' }}
                                >
                                    ‹ Trước
                                </Button>
                                <span className="font-weight-bold text-muted small">
                                    Học viên {mobileIndex + 1} / {filteredItems.length}
                                </span>
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    disabled={mobileIndex >= filteredItems.length - 1}
                                    onClick={() => setMobileIndex(prev => Math.min(filteredItems.length - 1, prev + 1))}
                                    style={{ fontSize: '12px' }}
                                >
                                    Sau ›
                                </Button>
                            </div>
                        )}

                        {/* 6. Card Chi Tiết Học Viên */}
                        {currentMobileItem ? (
                            (() => {
                                const originalIdx = items.findIndex(x => (x.hoSoTuyenSinhId && x.hoSoTuyenSinhId === currentMobileItem.hoSoTuyenSinhId) || x.id === currentMobileItem.id);
                                const idxToUpdate = originalIdx !== -1 ? originalIdx : mobileIndex;
                                return (
                                    <div className="bg-white p-3 border rounded-lg shadow-sm mb-3" style={{ borderRadius: '14px' }}>
                                        {/* Header học viên */}
                                        <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-3">
                                            <div className="d-flex align-items-center">
                                                <div
                                                    className="d-flex align-items-center justify-content-center mr-2 font-weight-bold text-primary"
                                                    style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#e0f2fe', fontSize: '15px' }}
                                                >
                                                    {getInitials(currentMobileItem.hoTen)}
                                                </div>
                                                <div>
                                                    <div className="font-weight-bold text-dark" style={{ fontSize: '15px' }}>
                                                        {currentMobileItem.hoTen}
                                                    </div>
                                                    <div className="text-muted small" style={{ fontSize: '11px' }}>
                                                        {currentMobileItem.soCccd ? `CCCD: ${currentMobileItem.soCccd} · ` : ''}
                                                        {currentMobileItem.ngaySinh ? moment(currentMobileItem.ngaySinh).format('DD/MM/YYYY') : ''}
                                                    </div>
                                                </div>
                                            </div>
                                            <div>
                                                {currentMobileItem.ketQuaDanhGia === 'Đạt' && (
                                                    <span className="badge px-2 py-1" style={{ backgroundColor: '#dcfce7', color: '#15803d', fontSize: '12px' }}>
                                                        Đạt
                                                    </span>
                                                )}
                                                {currentMobileItem.ketQuaDanhGia === 'Không đạt' && (
                                                    <span className="badge px-2 py-1" style={{ backgroundColor: '#fee2e2', color: '#b91c1c', fontSize: '12px' }}>
                                                        Không đạt
                                                    </span>
                                                )}
                                                {(!currentMobileItem.ketQuaDanhGia || currentMobileItem.ketQuaDanhGia === '') && (
                                                    <span className="badge px-2 py-1" style={{ backgroundColor: '#f1f5f9', color: '#64748b', fontSize: '12px' }}>
                                                        Chưa đánh giá
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {/* Form fields */}
                                        <Form.Group className="mb-2">
                                            <Form.Label className="font-weight-bold small text-dark mb-1" style={{ fontSize: '12px' }}>
                                                1. Năng lực thực hành chuyên khoa:
                                            </Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                placeholder="Nhập nhận xét năng lực chuyên môn..."
                                                value={currentMobileItem.nangLucChuyenMon || ''}
                                                onChange={(e) => handleFieldChange(idxToUpdate, 'nangLucChuyenMon', e.target.value)}
                                                style={{ fontSize: '13px' }}
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-2">
                                            <Form.Label className="font-weight-bold small text-dark mb-1" style={{ fontSize: '12px' }}>
                                                2. Ý thức, tổ chức kỷ luật:
                                            </Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                placeholder="Nhập nhận xét ý thức kỷ luật..."
                                                value={currentMobileItem.yThucKyLuat || ''}
                                                onChange={(e) => handleFieldChange(idxToUpdate, 'yThucKyLuat', e.target.value)}
                                                style={{ fontSize: '13px' }}
                                            />
                                        </Form.Group>

                                        <Form.Group className="mb-2">
                                            <Form.Label className="font-weight-bold small text-dark mb-1" style={{ fontSize: '12px' }}>
                                                Kết quả:
                                            </Form.Label>
                                            <Form.Control
                                                as="select"
                                                value={currentMobileItem.ketQuaDanhGia || ''}
                                                onChange={(e) => handleFieldChange(idxToUpdate, 'ketQuaDanhGia', e.target.value)}
                                                className={`font-weight-bold ${currentMobileItem.ketQuaDanhGia === 'Đạt' ? 'text-success' :
                                                        currentMobileItem.ketQuaDanhGia === 'Không đạt' ? 'text-danger' : ''
                                                    }`}
                                                style={{ fontSize: '13px' }}
                                            >
                                                <option value="">-- Chưa đánh giá --</option>
                                                <option value="Đạt">Đạt</option>
                                                <option value="Không đạt">Không đạt</option>
                                            </Form.Control>
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label className="font-weight-bold small text-dark mb-1" style={{ fontSize: '12px' }}>
                                                Nhận xét / Ghi chú thêm:
                                            </Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Ghi chú..."
                                                value={currentMobileItem.nhanXetChung || ''}
                                                onChange={(e) => handleFieldChange(idxToUpdate, 'nhanXetChung', e.target.value)}
                                                style={{ fontSize: '13px' }}
                                            />
                                        </Form.Group>

                                        {/* 3 Action Buttons Footer */}
                                        <div className="d-flex">
                                            <Button
                                                variant="outline-secondary"
                                                className="w-25 py-2 mr-2"
                                                style={{ fontSize: '13px' }}
                                                onClick={() => refetchDotData()}
                                            >
                                                Hủy
                                            </Button>
                                            <Button
                                                variant="primary"
                                                className="w-50 py-2 font-weight-bold mr-2"
                                                style={{ fontSize: '13px' }}
                                                onClick={() => handleSave(true)}
                                                disabled={isSaving}
                                            >
                                                <i className={`fas fa-save mr-1 ${isSaving ? 'fa-spin' : ''}`}></i> Lưu đợt
                                            </Button>
                                            <Button
                                                variant="primary"
                                                className="w-50 py-2 font-weight-bold"
                                                style={{ fontSize: '13px', backgroundColor: '#2563eb' }}
                                                onClick={async () => {
                                                    const saved = await handleSave(false);
                                                    if (saved) {
                                                        if (mobileIndex < filteredItems.length - 1) {
                                                            setMobileIndex(prev => prev + 1);
                                                            toast.success(`Đã lưu & chuyển sang học viên ${mobileIndex + 2}/${filteredItems.length}`);
                                                        } else {
                                                            toast.success("Đã lưu học viên cuối cùng trong đợt!");
                                                        }
                                                    }
                                                }}
                                                disabled={isSaving}
                                            >
                                                Lưu & tiếp ›
                                            </Button>
                                        </div>
                                    </div>
                                );
                            })()
                        ) : (
                            <div className="text-center py-4 bg-white rounded border">
                                <div className="text-muted mb-2">Chưa có học viên nào trong đợt này.</div>
                                <Button
                                    size="sm"
                                    variant="primary"
                                    onClick={() => syncMutation.mutate()}
                                    disabled={syncMutation.isLoading || !selectedBatchId}
                                >
                                    <i className={`fas fa-sync-alt mr-1 ${syncMutation.isLoading ? 'fa-spin' : ''}`}></i>
                                    Đồng bộ danh sách
                                </Button>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Modal Import Excel */}
            {showImportModal && (
                <ImportBangDanhGiaThucHanhModal
                    show={showImportModal}
                    onHide={() => setShowImportModal(false)}
                    lopId={lopId}
                    batchId={selectedBatchId}
                    onApply={handleApplyImport}
                />
            )}
        </div>
    );
};

export default BangDanhGiaThucHanhTab;
